/**
 * AI Communications Service
 * Provides language detection, translation, and AI response suggestions for 2-way conversations
 */

import axios from 'axios';

import { config as productConfig } from '@/src/config';
const API_URL = (productConfig.weaveinApi.baseUrl ?? 'http://localhost:8000').trim();

const getHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Types
export interface LanguageDetection {
  language: string;
  language_name: string;
  confidence: number;
}

export interface Translation {
  original_text: string;
  translated_text: string;
  source_language: string;
  target_language: string;
}

export interface ResponseSuggestion {
  suggestions: string[];
  confidence: number;
}

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Detect the language of a given text
 */
export const detectLanguage = async (text: string): Promise<LanguageDetection> => {
  const response = await axios.post(
    `${API_URL}/api/ai-communications/detect-language`,
    { text },
    { headers: getHeaders() }
  );
  return response.data;
};

/**
 * Translate text between languages
 */
export const translateText = async (
  text: string,
  targetLanguage: string = 'en',
  sourceLanguage?: string
): Promise<Translation> => {
  const response = await axios.post(
    `${API_URL}/api/ai-communications/translate`,
    {
      text,
      target_language: targetLanguage,
      source_language: sourceLanguage
    },
    { headers: getHeaders() }
  );
  return response.data;
};

/**
 * Get AI-suggested responses based on conversation history
 */
export const getSuggestedResponses = async (
  conversationHistory: ConversationMessage[],
  context?: string,
  language: string = 'en'
): Promise<ResponseSuggestion> => {
  const response = await axios.post(
    `${API_URL}/api/ai-communications/suggest-responses`,
    {
      conversation_history: conversationHistory,
      context,
      language
    },
    { headers: getHeaders() }
  );
  return response.data;
};

/**
 * Helper: Get language flag emoji
 */
export const getLanguageFlag = (languageCode: string): string => {
  const flags: Record<string, string> = {
    'en': '🇺🇸',
    'es': '🇪🇸',
    'zh': '🇨🇳',
    'fr': '🇫🇷',
    'de': '🇩🇪',
    'ar': '🇸🇦',
    'hi': '🇮🇳',
    'pt': '🇵🇹',
    'ru': '🇷🇺',
    'ja': '🇯🇵',
    'ko': '🇰🇷',
    'vi': '🇻🇳',
    'tl': '🇵🇭'
  };
  return flags[languageCode] || '🌐';
};

/**
 * Helper: Get language display name
 */
export const getLanguageName = (languageCode: string): string => {
  const names: Record<string, string> = {
    'en': 'English',
    'es': 'Spanish',
    'zh': 'Chinese',
    'fr': 'French',
    'de': 'German',
    'ar': 'Arabic',
    'hi': 'Hindi',
    'pt': 'Portuguese',
    'ru': 'Russian',
    'ja': 'Japanese',
    'ko': 'Korean',
    'vi': 'Vietnamese',
    'tl': 'Tagalog'
  };
  return names[languageCode] || languageCode;
};
